import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

export const setAuthToken = (token) => {
  if (token) client.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete client.defaults.headers.common["Authorization"];
};

// Attach the saved token synchronously at module load — BEFORE any component
// mounts or React Query fires — so the very first authed request carries the
// Bearer header. (Previously the token was only set in an AuthContext useEffect,
// so the first /admin request went out unauthenticated → 401 → spinner.)
try {
  const _saved = JSON.parse(localStorage.getItem("alphaapeing_auth") || "null");
  if (_saved?.token) setAuthToken(_saved.token);
} catch (_) {}

// If a request that ACTUALLY carried an auth header still 401s, the session is
// dead (expired/invalid) — drop it and reload once so the app stops treating the
// user as logged in (prevents Admin spinning forever on a stale token). We only
// act when the failed request had an Authorization header, so a race where an
// unauthenticated request 401s never wipes a valid session.
client.interceptors.response.use(
  (r) => r,
  (err) => {
    const hadAuth = !!(err?.config?.headers?.Authorization || err?.config?.headers?.authorization);
    if (err?.response?.status === 401 && hadAuth) {
      try {
        if (localStorage.getItem("alphaapeing_auth")) {
          localStorage.removeItem("alphaapeing_auth");
          if (!window.__aaAuthExpired) {
            window.__aaAuthExpired = true;
            setAuthToken(null);
            window.location.reload();
          }
        }
      } catch (_) {}
    }
    return Promise.reject(err);
  }
);

export const authRegister = (email, password) => client.post("/auth/register", { email, password }).then((r) => r.data);
export const authLogin = (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data);
export const authVerify = (email, code) => client.post("/auth/verify", { email, code }).then((r) => r.data);
export const authResend = (email) => client.post("/auth/resend-code", { email }).then((r) => r.data);
export const authMe = () => client.get("/auth/me").then((r) => r.data);

export const trackPageView = (path, clientId, extra = {}) => client.post("/track/pageview", { path, clientId, ...extra }).catch(() => {});
export const trackEvent = (event, clientId, meta = {}) => client.post("/track/event", { event, clientId, meta }).catch(() => {});
export const reportIssue = (payload) => client.post("/track/issue", payload).then((r) => r.data);
export const fetchAdminMetrics = () => client.get("/admin/metrics").then((r) => r.data);
export const fetchAdminIssues = () => client.get("/admin/issues").then((r) => r.data);
export const resolveIssue = (id) => client.post("/admin/issues/resolve", { id }).then((r) => r.data);
export const fetchHandleWatch = (cid, showAll = false, provenOnly = false, minScore = 0) => client.get(`/handles/${cid}`, { params: { ...(showAll ? { show_all: true } : {}), ...(provenOnly ? { proven_only: true } : {}), ...(minScore ? { min_score: minScore } : {}) }, timeout: 20000 }).then((r) => r.data);
export const fetchProvenBuilders = (limit = 12, follow = "") => client.get("/proven-builders", { params: { limit, ...(follow ? { follow } : {}) }, timeout: 25000 }).then((r) => r.data);
export const fetchProvenDevs = () => client.get("/proven-devs", { timeout: 25000 }).then((r) => r.data);
export const fetchBreakoutRadar = () => client.get("/breakout-radar", { timeout: 25000 }).then((r) => r.data);
export const fetchSurvivorWatch = () => client.get("/survivor-watch", { timeout: 20000 }).then((r) => r.data);
export const addSurvivorWatch = (input) => client.post("/survivor-watch", { input }, { timeout: 30000 }).then((r) => r.data);
export const removeSurvivorWatch = (mint) => client.delete(`/survivor-watch/${mint}`, { timeout: 15000 }).then((r) => r.data);
export const fetchGithubRadar = () => client.get("/github-radar", { timeout: 30000 }).then((r) => r.data);
export const fetchMorningCall = () => client.get("/morning-call", { timeout: 25000 }).then((r) => r.data);
export const fetchTgChannels = () => client.get("/telegram/channels", { timeout: 15000 }).then((r) => r.data);
export const addTgChannel = (handle) => client.post("/telegram/channels", { handle }, { timeout: 20000 }).then((r) => r.data);
export const removeTgChannel = (handle) => client.delete(`/telegram/channels/${encodeURIComponent(handle.replace(/^@/, ""))}`, { timeout: 15000 }).then((r) => r.data);
export const fetchTgSignals = () => client.get("/telegram/signals", { timeout: 15000 }).then((r) => r.data);
export const fetchMtfRadar = () => client.get("/mtf-radar", { timeout: 25000 }).then((r) => r.data);
export const lookupMtf = (mint) => client.get(`/mtf/${mint.trim()}`, { timeout: 25000 }).then((r) => r.data);
export const fetchBuilderFollows = (clientId) => client.get(`/builder-follows/${clientId}`, { timeout: 15000 }).then((r) => r.data);
export const toggleBuilderFollow = (clientId, creator) => client.post(`/builder-follows/${clientId}`, { creator }, { timeout: 15000 }).then((r) => r.data);
export const fetchTrendingThemes = () => client.get("/trending-themes", { timeout: 30000 }).then((r) => r.data);
export const fetchTrendingWorld = () => client.get("/trending-world", { timeout: 60000 }).then((r) => r.data);
export const fetchMyBuilders = (clientId) => client.get(`/my-builders/${clientId}`, { timeout: 25000 }).then((r) => r.data);
export const fetchLaunchHub = (clientId) => client.get(`/launch-hub/${clientId}`, { timeout: 30000 }).then((r) => r.data);
export const setBuilderLabel = (clientId, creator, label) => client.post(`/builder-labels/${clientId}`, { creator, label }, { timeout: 15000 }).then((r) => r.data);
export const setBuilderNote = (clientId, creator, note) => client.post(`/builder-labels/${clientId}`, { creator, note }, { timeout: 15000 }).then((r) => r.data);
export const fetchDeployer = (creator) => client.get(`/deployer/${creator}`, { timeout: 20000 }).then((r) => r.data);
export const fetchRunnerLeaderboard = (limit = 50) => client.get("/runner-leaderboard", { params: { limit }, timeout: 25000 }).then((r) => r.data);
export const addHandle = (cid, handle) => client.post(`/handles/${cid}`, { handle }).then((r) => r.data);
export const removeHandle = (cid, handle) => client.delete(`/handles/${cid}/${handle}`).then((r) => r.data);
export const fetchAdminStats = () => client.get("/admin/stats").then((r) => r.data);
export const fetchAdminSettings = () => client.get("/admin/settings").then((r) => r.data);
export const saveAdminSettings = (mode) => client.post("/admin/settings", { mode }).then((r) => r.data);

export const fetchTrash = (clientId) => client.get(`/trash/${clientId}`).then((r) => r.data);
export const addTrash = (clientId, payload) => client.post(`/trash/${clientId}`, payload).then((r) => r.data);
export const removeTrash = (clientId, mint) => client.delete(`/trash/${clientId}/${mint}`).then((r) => r.data);
export const fetchCommunityTrash = () => client.get("/community-trash").then((r) => r.data);
export const fetchTrashGraveyard = () => client.get("/admin/trash-graveyard").then((r) => r.data);
export const clearGraveyard = (mint) => client.post("/admin/trash-graveyard/clear", { mint }).then((r) => r.data);
export const fetchCuratedWallets = () => client.get("/admin/curated-wallets").then((r) => r.data);
export const addCuratedWallet = (address, label) => client.post("/admin/curated-wallets", { address, label }).then((r) => r.data);
export const deleteCuratedWallet = (address) => client.delete(`/admin/curated-wallets/${address}`).then((r) => r.data);

export const fetchHealthLimits = () => client.get("/health/limits", { timeout: 12000 }).then((r) => r.data);

export const fetchCto = () => client.get("/cto", { timeout: 40000 }).then((r) => r.data);
export const fetchHolderFlow = (address) => client.get(`/coins/${address}/holder-flow`, { timeout: 40000 }).then((r) => r.data);
export const watchCreatorAlert = (clientId, address, symbol = "") => client.post(`/alerts/${clientId}/watch-creator`, { address, symbol }).then((r) => r.data);
export const fetchTopHolder = (address) => client.get(`/coins/${address}/top-holder`, { timeout: 45000 }).then((r) => r.data);
export const fetchHandleCount = (cid, minScore = 0) => client.get(`/handles/${cid}/count`, { params: { min_score: minScore }, timeout: 20000 }).then((r) => r.data);
export const fetchAdminCto = () => client.get("/admin/cto").then((r) => r.data);
export const tagCto = (body) => client.post("/admin/cto", body).then((r) => r.data);
export const untagCto = (mint) => client.delete(`/admin/cto/${mint}`).then((r) => r.data);

export const fetchCoins = async (params = {}) => {
  const { data } = await client.get("/coins", { params });
  return data;
};

export const fetchStats = async (chain = "solana") => {
  const { data } = await client.get("/stats", { params: { chain } });
  return data;
};

export const fetchCoin = async (address) => {
  const { data } = await client.get(`/coins/${address}`);
  return data;
};

export const fetchWatchlist = async (addresses) => {
  const { data } = await client.post("/watchlist", { addresses });
  return data;
};

export const getWatchlistItems = (cid) => client.get(`/watchlist/${cid}`).then((r) => r.data);
export const fetchCommandCenter = (cid, limit = 60) => client.get(`/command-center/${cid}`, { params: { limit }, timeout: 25000 }).then((r) => r.data);
export const fetchClusters = (cid) => client.get(`/clusters/${cid}`).then((r) => r.data);
export const fetchMillyBoard = (cid, windowH = 24, minSol = 5) => client.get(`/milly/${cid}`, { params: { window: windowH, min_sol: minSol }, timeout: 25000 }).then((r) => r.data);
export const fetchEarlyGambles = (chain = "solana") => client.get("/early-gambles", { params: { chain } }).then((r) => r.data);
export const fetchGraduates = () => client.get("/graduates").then((r) => r.data);
export const fetchTrendingOrganic = () => client.get("/trending-organic").then((r) => r.data);
export const fetchOrganicRunners = () => client.get("/organic-runners").then((r) => r.data);
export const fetchCandleRadar = () => client.get("/candle-radar").then((r) => r.data);
export const fetchEarlyRunners = () => client.get("/early-runners").then((r) => r.data);
export const fetchRobinhoodLaunches = (q = "") => client.get("/robinhood/launches", { params: q ? { q } : {}, timeout: 30000 }).then((r) => r.data);
export const fetchRobinhoodWallets = (clientId) => client.get("/robinhood/wallets", { params: { client_id: clientId }, timeout: 30000 }).then((r) => r.data);
export const addRobinhoodWallet = (clientId, address, label) => client.post("/robinhood/wallets", { clientId, address, label }).then((r) => r.data);
export const deleteRobinhoodWallet = (clientId, address) => client.delete(`/robinhood/wallets/${clientId}/${address}`).then((r) => r.data);
export const fetchRobinhoodFresh = () => client.get("/robinhood/fresh", { timeout: 30000 }).then((r) => r.data);
export const fetchRobinhoodSurge = (window = 90) => client.get("/robinhood/surge", { params: { window }, timeout: 30000 }).then((r) => r.data);
export const fetchRobinhoodPulse = () => client.get("/robinhood/pulse", { timeout: 20000 }).then((r) => r.data);
export const fetchRhWatchlists = (clientId) => client.get(`/robinhood/watchlists/${clientId}`).then((r) => r.data);
export const saveRhWatchlist = (clientId, body) => client.post(`/robinhood/watchlists/${clientId}`, body).then((r) => r.data);
export const toggleRhWatchlist = (clientId, wid) => client.post(`/robinhood/watchlists/${clientId}/${wid}/toggle`).then((r) => r.data);
export const deleteRhWatchlist = (clientId, wid) => client.delete(`/robinhood/watchlists/${clientId}/${wid}`).then((r) => r.data);
export const addWatchlistItem = (cid, address) => client.post(`/watchlist/${cid}`, { address }).then((r) => r.data);
export const removeWatchlistItem = (cid, address) => client.delete(`/watchlist/${cid}/${address}`).then((r) => r.data);

export const fetchAlertConfig = async () => {  const { data } = await client.get("/alerts/config");
  return data;
};

export const fetchAlertSettings = async (clientId) => {
  const { data } = await client.get(`/alerts/${clientId}`);
  return data;
};

export const saveAlertSettings = async (clientId, settings) => {
  const { data } = await client.post(`/alerts/${clientId}`, settings);
  return data;
};

export const sendTestAlert = async (clientId) => {
  const { data } = await client.post(`/alerts/${clientId}/test`);
  return data;
};

export const sendDeployerTestAlert = async (clientId) => {
  const { data } = await client.post(`/alerts/${clientId}/test-deployer`);
  return data;
};

export const sendTestDigest = async (clientId) => {
  const { data } = await client.post(`/alerts/${clientId}/test-digest`);
  return data;
};

export const fetchHeliusAccess = async () => (await client.get("/helius-access/me")).data;
export const requestHeliusAccess = async () => (await client.post("/helius-access/request")).data;
export const fetchHeliusUsers = async () => (await client.get("/admin/helius-access")).data;
export const setHeliusUserAccess = async (userId, access) => (await client.post(`/admin/helius-access/${userId}`, { access })).data;

export const fetchSmartMoneyConfig = async () => {
  const { data } = await client.get("/smart-money/config");
  return data;
};

export const fetchTrackedWallets = async (clientId) => {
  const { data } = await client.get(`/smart-money/${clientId}/wallets`);
  return data;
};

export const addTrackedWallet = async (clientId, address, label) => {
  const { data } = await client.post(`/smart-money/${clientId}/wallets`, { address, label });
  return data;
};

export const removeTrackedWallet = async (clientId, address) => {
  const { data } = await client.delete(`/smart-money/${clientId}/wallets/${address}`);
  return data;
};

export const fetchWalletFeed = async (clientId) => {
  const { data } = await client.get(`/smart-money/${clientId}/feed`);
  return data;
};

export const fetchWalletPnl = async (clientId, address) => {
  const { data } = await client.get(`/smart-money/${clientId}/wallets/${address}/pnl`);
  return data;
};

export const fetchWalletBuys = async (clientId) => {
  const { data } = await client.get(`/smart-money/${clientId}/buys`);
  return data;
};

export const fetchRugcheck = async (address) => {
  const { data } = await client.get(`/coins/${address}/rugcheck`);
  return data;
};
export const fetchDeployerFunding = (wallet) => client.get(`/deployer-funding/${wallet}`, { timeout: 45000 }).then((r) => r.data);
export const fetchFundingWeb = () => client.get("/funding-web", { timeout: 30000 }).then((r) => r.data);
export const fetchDeployerStatsBatch = (creators) => client.post("/deployer-stats/batch", { creators: (creators || []).slice(0, 60) }, { timeout: 30000 }).then((r) => r.data);
export const batchFollowBuilders = (clientId, creators) => client.post(`/builder-follows/${clientId}/batch`, { creators }, { timeout: 20000 }).then((r) => r.data);
export const fetchFreshDrops = (clientId) => client.get(`/fresh-drops/${clientId}`, { timeout: 30000 }).then((r) => r.data);
export const fetchFreshDropsLatest = (clientId) => client.get(`/fresh-drops/${clientId}/latest`, { timeout: 12000 }).then((r) => r.data);
export const setDropVerdict = (payload) => client.post("/drop-verdict", payload, { timeout: 15000 }).then((r) => r.data);
export const fetchWalletScout = (mint) => client.get(`/wallet-scout/${mint}`, { timeout: 45000 }).then((r) => r.data);
export const fetchInsiderRadar = () => client.get("/insider-radar", { timeout: 20000 }).then((r) => r.data);
export const fetchScreener = (params) => {
  const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== "" && v != null));
  return client.get("/screener", { params: clean, timeout: 20000 }).then((r) => r.data);
};
export const fetchScreenerAlerts = (clientId) => client.get(`/screener-alerts/${clientId}`, { timeout: 15000 }).then((r) => r.data);
export const fetchWaitingRoom = () => client.get("/waiting-room", { timeout: 20000 }).then((r) => r.data);
export const dismissWaiter = (mint) => client.delete(`/waiting-room/${mint}`, { timeout: 15000 }).then((r) => r.data);
export const saveWaiterSettings = (body) => client.post("/waiting-room/settings", body, { timeout: 15000 }).then((r) => r.data);
export const fetchTier3 = () => client.get("/tier3", { timeout: 20000 }).then((r) => r.data);
export const saveTier3Settings = (body) => client.post("/tier3/settings", body, { timeout: 15000 }).then((r) => r.data);
export const fetchTier3Map = () => client.get("/tier3/map", { timeout: 15000 }).then((r) => r.data);
export const fetchTier3Alerts = () => client.get("/tier3/alerts", { timeout: 15000 }).then((r) => r.data);
export const fetchKols = () => client.get("/kols", { timeout: 15000 }).then((r) => r.data);
export const addKol = (handle) => client.post("/kols", { handle }, { timeout: 15000 }).then((r) => r.data);
export const removeKol = (handle) => client.delete(`/kols/${handle}`, { timeout: 15000 }).then((r) => r.data);
export const hypePreview = (mint) => client.post("/hype/preview", { mint }, { timeout: 20000 }).then((r) => r.data);
export const hypeJob = (id) => client.get(`/hype/job/${id}`, { timeout: 15000 }).then((r) => r.data);
export const hypePost = (mint, text) => client.post("/hype/post", { mint, text }, { timeout: 45000 }).then((r) => r.data);
export const fetchHypeHistory = () => client.get("/hype/history", { timeout: 15000 }).then((r) => r.data);
export const saveScreenerAlert = (clientId, name, filters) => client.post(`/screener-alerts/${clientId}`, { name, filters }, { timeout: 15000 }).then((r) => r.data);
export const deleteScreenerAlert = (clientId, alertId) => client.delete(`/screener-alerts/${clientId}/${alertId}`, { timeout: 15000 }).then((r) => r.data);

export const fetchCabalScan = (address, clientId) =>
  client.get(`/coins/${address}/cabal`, { params: { client_id: clientId }, timeout: 45000 }).then((r) => r.data);
export const fetchCabalRadar = () => client.get("/cabal-radar", { timeout: 25000 }).then((r) => r.data);
export const trackCabal = (cid, clusterId) => client.post(`/smart-money/${cid}/track-cabal`, { clusterId }).then((r) => r.data);
export const fetchCabalProfile = (clusterId) => client.get(`/cabal/${clusterId}`, { timeout: 25000 }).then((r) => r.data);
export const fetchQualityStats = () => client.get("/quality-stats", { timeout: 15000 }).then((r) => r.data);
export const fetchConvictionStats = () => client.get("/conviction-stats", { timeout: 15000 }).then((r) => r.data);
export const fetchWatchlistRunners = (cid) => client.get(`/watchlist-runners/${cid}`, { timeout: 20000 }).then((r) => r.data);
export const fetchTodaysRunner = () => client.get("/todays-runner", { timeout: 20000 }).then((r) => r.data);
export const fetchTechTokens = () => client.get("/tech-tokens", { timeout: 20000 }).then((r) => r.data);
export const fetchDevLeaderboard = (cid) => client.get(`/dev-leaderboard/${cid}`, { timeout: 20000 }).then((r) => r.data);
export const fetchScoreHistory = (mint) => client.get(`/score-history/${mint}`, { timeout: 15000 }).then((r) => r.data);
export const fetchScoreRetrospective = () => client.get("/score-retrospective", { timeout: 25000 }).then((r) => r.data);
export const fetchEditorialIndex = () => client.get("/editorial", { timeout: 15000 }).then((r) => r.data);
export const fetchEditorial = (fmt, refresh = false) => client.get(`/editorial/${fmt}`, { params: refresh ? { refresh: true } : {}, timeout: 60000 }).then((r) => r.data);
export const generateCustomEditorial = (payload) => client.post("/editorial/custom", payload, { timeout: 20000 }).then((r) => r.data);
export const fetchCustomStoryJob = (sid) => client.get(`/editorial-custom/job/${sid}`, { timeout: 15000 }).then((r) => r.data);
export const fetchStoryScheduler = () => client.get("/newsroom/scheduler", { timeout: 15000 }).then((r) => r.data);
export const setStoryScheduler = (payload) => client.post("/newsroom/scheduler", payload, { timeout: 15000 }).then((r) => r.data);
export const schedulerPostNow = () => client.post("/newsroom/scheduler/post-now", {}, { timeout: 60000 }).then((r) => r.data);
export const fetchLatestCustomEditorial = () => client.get("/editorial-custom/latest", { timeout: 15000 }).then((r) => r.data);
export const fetchCustomStoryLibrary = (q = "") => client.get("/editorial-custom/list", { params: q ? { q } : {}, timeout: 15000 }).then((r) => r.data);
export const fetchCustomStory = (sid) => client.get(`/editorial-custom/story/${sid}`, { timeout: 15000 }).then((r) => r.data);
export const deleteCustomStory = (sid) => client.delete(`/editorial-custom/story/${sid}`, { timeout: 15000 }).then((r) => r.data);
export const fetchPriceSeries = (address, tf = "5m") => client.get(`/coins/${address}/price-series`, { params: { tf }, timeout: 30000 }).then((r) => r.data);
export const fetchCoinTech = (address) => client.get(`/coins/${address}/tech`, { timeout: 20000 }).then((r) => r.data);
export const fetchRunnerRecap = () => client.get("/runner-recap", { timeout: 20000 }).then((r) => r.data);
export const fetchTechRecap = () => client.get("/tech-recap", { timeout: 20000 }).then((r) => r.data);
export const fetchWinnerCobuys = (cid) => client.get(`/winner-cobuys/${cid}`, { timeout: 25000 }).then((r) => r.data);

export const fetchIndicators = async (address, tf = "1h") => {
  const { data } = await client.get(`/coins/${address}/indicators`, { params: { tf } });
  return data;
};

export const fetchRugs = async (clientId) => {
  const { data } = await client.get(`/rugs/${clientId}`);
  return data;
};

export const flagRug = async (clientId, payload) => {
  const { data } = await client.post(`/rugs/${clientId}`, payload);
  return data;
};

export const unflagRug = async (clientId, mint) => {
  const { data } = await client.delete(`/rugs/${clientId}/${mint}`);
  return data;
};

export const fetchTrustedCreators = async (clientId) => {
  const { data } = await client.get(`/creators/${clientId}`);
  return data;
};

export const addTrustedCreator = async (clientId, payload) => {
  const { data } = await client.post(`/creators/${clientId}`, payload);
  return data;
};

export const delTrustedCreator = async (clientId, creator) => {
  const { data } = await client.delete(`/creators/${clientId}/${creator}`);
  return data;
};

export const nameBlockedCreator = async (clientId, payload) => {
  const { data } = await client.post(`/rugs/${clientId}/name`, payload);
  return data;
};

export const swapDev = async (clientId, payload) => {
  const { data } = await client.post(`/creators/${clientId}/swap`, payload);
  return data;
};

export const fetchPumpLaunches = async (sort = "curve") => {
  const { data } = await client.get(`/pump/launches`, { params: { sort } });
  return data;
};

export const fetchLeaderboard = async () => {
  const { data } = await client.get("/leaderboard");
  return data;
};

export const fetchHiddenWallets = async () => {
  const { data } = await client.get("/removal/hidden");
  return data;
};

export const submitRemovalRequest = async (payload) => {
  const { data } = await client.post("/removal/request", payload);
  return data;
};

export const fetchAdminRemovals = async () => {
  const { data } = await client.get("/removal/admin/list");
  return data;
};

export const restoreRemoval = async (wallet) => {
  const { data } = await client.post("/removal/admin/restore", { wallet });
  return data;
};

export const hideRemoval = async (wallet) => {
  const { data } = await client.post("/removal/admin/hide", { wallet });
  return data;
};
